import {
  clearSilentGuard,
  ensureSession,
  hasTriedSilent,
  markSilentTried,
} from "@foxia/web-sdk/silent-sso";

import { client } from "@/api/api";

export interface SilentCallbackToken {
  code: string;
  state: string | null;
  codeVerifier?: string;
}

const PASSIVE_AUTH_COOKIE = "foxia_passive_auth_checked";
const PASSIVE_AUTH_HOST_COOKIE = "__Host-foxia_passive_auth_checked";
const PASSIVE_AUTH_FLOW_KEY = "foxia_passive_auth_flow";
const PASSIVE_AUTH_COOLDOWN_SECONDS = 15 * 60;
type SilentPrepareResponse = { auth_url?: string; auth_endpoint?: string };
let silentPrepareInFlight: Promise<{ data: SilentPrepareResponse }> | null = null;

function setPassiveAuthFlow(active: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (active) {
      window.sessionStorage.setItem(PASSIVE_AUTH_FLOW_KEY, "1");
    } else {
      window.sessionStorage.removeItem(PASSIVE_AUTH_FLOW_KEY);
    }
  } catch {
    // sessionStorage can be unavailable in restricted browser contexts.
  }
}

function hasActivePassiveAuthFlow(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(PASSIVE_AUTH_FLOW_KEY) === "1";
  } catch {
    return false;
  }
}

function passiveAuthCookieName(): string {
  return typeof window !== "undefined" && window.location.protocol === "https:"
    ? PASSIVE_AUTH_HOST_COOKIE
    : PASSIVE_AUTH_COOKIE;
}

export function isLikelyBotUserAgent(userAgent?: string): boolean {
  const value = userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
  return /bot|crawler|spider|preview|slackbot|facebookexternalhit|whatsapp|linkedinbot/i.test(value);
}

function canAttemptPassiveAuth(): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (document.visibilityState !== "visible") return false;
  if (window.top !== window.self) return false;
  return !isLikelyBotUserAgent();
}

function getSilentAuthOptions() {
  const env = import.meta.env as Record<string, string | undefined>;
  const configuredClientId = env.VITE_OIDC_CLIENT_ID?.trim();
  return {
    clientId: configuredClientId || "gateway-managed",
    scope: env.VITE_OIDC_SCOPE ?? "openid offline_access email",
    redirectUri: `${window.location.origin}/auth/callback`,
    authEndpoint: "/api/gateway/auth/silent-start",
    exchangeEndpoint: "/api/gateway/auth/exchange",
    pkce: true,
  };
}

type SilentAuthAttemptState =
  | "redirecting"
  | "login_required"
  | "cancelled";

export async function trySilentAuth(
  shouldCancel?: () => boolean,
  navigate: (url: string) => void = (url) => window.location.assign(url),
): Promise<SilentAuthAttemptState> {
  if (hasTriedSilent()) return "login_required";
  if (shouldCancel?.()) return "cancelled";
  try {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (!silentPrepareInFlight) {
      silentPrepareInFlight = client.post<SilentPrepareResponse>(
        "/gateway/auth/silent-prepare",
        { return_to: returnTo },
      );
    }
    const prepareRequest = silentPrepareInFlight;
    let response: { data: SilentPrepareResponse };
    try {
      response = await prepareRequest;
    } finally {
      if (silentPrepareInFlight === prepareRequest) {
        silentPrepareInFlight = null;
      }
    }
    if (shouldCancel?.()) {
      return "cancelled";
    }
    // A concurrent caller may already own the redirect after awaiting the
    // same prepare request. Report the shared flow without redirecting twice.
    if (hasTriedSilent()) return "redirecting";
    if (typeof response.data.auth_url === "string" && response.data.auth_url.length > 0) {
      // The Gateway owns the whole callback for this flow and redirects back
      // to return_to after consuming the state. Do not leave the legacy
      // frontend callback marker behind when OAuth returns an error.
      markSilentTried();
      navigate(response.data.auth_url);
      return "redirecting";
    }
    if (typeof response.data.auth_endpoint !== "string" || response.data.auth_endpoint.length === 0) {
      throw new Error("Silent authentication URL is missing");
    }
    const options = getSilentAuthOptions();
    options.authEndpoint = response.data.auth_endpoint;
    setPassiveAuthFlow(true);
    const state = await ensureSession(options);
    if (state !== "redirecting") setPassiveAuthFlow(false);
    return state === "redirecting" ? "redirecting" : "login_required";
  } catch {
    markSilentTried();
    return "login_required";
  }
}

export function tryInteractiveAuth(
  navigate: (url: string) => void = (url) => window.location.assign(url),
): "redirecting" {
  setPassiveAuthFlow(false);
  clearSilentGuard();
  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const query = new URLSearchParams({ return_to: returnTo });
  navigate(`/api/gateway/auth/login?${query.toString()}`);
  return "redirecting";
}

type UnauthorizedRecoveryOptions =
  | { mode: "passive"; shouldCancel?: () => boolean; navigate?: (url: string) => void }
  | { mode: "interactive"; navigate?: (url: string) => void };

export async function recoverUnauthorizedSession(
  options: UnauthorizedRecoveryOptions,
) {
  if (options.mode === "passive") {
    return tryPassiveAuth(options.shouldCancel, options.navigate);
  }
  return tryInteractiveAuth(options.navigate);
}

export function hasPassiveAuthCooldown(): boolean {
  if (typeof document === "undefined") return true;
  const cookieName = passiveAuthCookieName();
  return document.cookie
    .split("; ")
    .some((row) => row === `${cookieName}=1`);
}

export function markPassiveAuthChecked(): void {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${passiveAuthCookieName()}=1; Max-Age=${PASSIVE_AUTH_COOLDOWN_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

/** Clear the per-tab loop guard while preserving the cross-page cooldown. */
export function resetPassiveAuthAttempt(): void {
  setPassiveAuthFlow(false);
  silentPrepareInFlight = null;
  clearSilentGuard();
}

export async function tryPassiveAuth(
  shouldCancel?: () => boolean,
  navigate?: (url: string) => void,
): Promise<
  | "cooldown"
  | "ineligible"
  | "redirecting"
  | "login_required"
  | "cancelled"
> {
  if (hasPassiveAuthCooldown()) return "cooldown";
  if (!canAttemptPassiveAuth()) return "ineligible";
  // A previous attempt in this tab already owns the flow. Do not create a
  // cross-tab cooldown from an untrusted callback URL.
  if (hasTriedSilent()) return "cooldown";

  const state = await trySilentAuth(shouldCancel, navigate);
  if (state !== "redirecting" && state !== "cancelled") markPassiveAuthChecked();
  return state;
}

export async function consumeAuthCallback(): Promise<SilentCallbackToken | null> {
  let token: SilentCallbackToken | null = null;
  await ensureSession({
    ...getSilentAuthOptions(),
    onResult: (result) => {
      if (result.token?.code && result.token.state) token = result.token;
    },
  });
  return token;
}

export function completeSilentAuth(): void {
  setPassiveAuthFlow(false);
  silentPrepareInFlight = null;
  clearSilentGuard();
}

export function isPassiveAuthFlowActive(): boolean {
  return hasActivePassiveAuthFlow();
}

/**
 * End a failed passive (background) probe: clear the per-tab guard and start the
 * cross-page cooldown so the landing page does not run the probe again and loop
 * back into the same failure.
 */
export function abandonPassiveAuth(): void {
  completeSilentAuth();
  markPassiveAuthChecked();
}

export function finishPassiveAuthFailure(oauthError: string): boolean {
  if (!hasActivePassiveAuthFlow()) return false;
  if (oauthError !== "login_required") {
    setPassiveAuthFlow(false);
    return false;
  }
  completeSilentAuth();
  markPassiveAuthChecked();
  return true;
}
